import Seo from "@/components/Seo";
import React, { useState } from "react";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-[#111827] border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#00F0FF] focus:outline-none";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact WorldAIInfo" description="Get in touch with the WorldAIInfo team — questions, partnerships, advertising and feedback." canonicalPath="/contact" />
      <section className="grid-bg border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
          <p className="font-mono-tech text-xs uppercase text-[#00F0FF] mb-3">Get in touch</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl">Contact Us</h1>
          <p className="mt-4 text-[#94A3B8] max-w-2xl">
            Questions, partnership enquiries, advertising, data requests, or feedback — send us a message and we'll respond as soon as we can.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        {done ? (
          <div className="card-surface rounded-2xl p-12 text-center" data-testid="contact-success">
            <div className="w-14 h-14 rounded-full bg-[#22C55E]/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-[#22C55E]" />
            </div>
            <h3 className="font-display font-bold text-2xl mb-2">Message sent</h3>
            <p className="text-[#94A3B8] max-w-md mx-auto">Thanks for reaching out. We've received your message and will respond to your email within 30 days.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="card-surface rounded-2xl p-8 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs text-[#94A3B8] mb-1.5 block">Your Name *</label>
                <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="contact-name" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1.5 block">Email Address *</label>
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="contact-email" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1.5 block">Subject *</label>
              <input required value={form.subject} onChange={(e) => set("subject", e.target.value)} className={inputCls} data-testid="contact-subject" />
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] mb-1.5 block">Message *</label>
              <textarea required rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} data-testid="contact-message" />
            </div>
            <button type="submit" disabled={busy} data-testid="contact-submit"
              className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-7 py-3 rounded-md hover:brightness-110 transition disabled:opacity-50">
              {busy ? "Sending…" : <>Send Message <Send className="w-4 h-4" /></>}
            </button>
          </form>
        )}

        <div className="mt-8 flex items-center gap-2 text-sm text-[#94A3B8]">
          <Mail className="w-4 h-4 text-[#00F0FF]" /> We respond to privacy and data requests within 30 days.
        </div>
      </div>
    </div>
  );
}
